import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import React, { useState, useCallback } from "react";
import { Feather, MaterialIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { router, useFocusEffect } from "expo-router";
import { Toast } from "react-native-toast-notifications";
import StandardNavigation from "../../../../components/navigation/StandardNavigation";
import i18n from "../../../../locales/i18n";
import customAxios from "../../../../utils/customAxios";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { appActions } from "../../../../store/slices/appSlice";
import WebPasswordScreen from "../../../../components/web/WebPasswordScreen";
import Animated, { FadeInDown } from "react-native-reanimated";

const ChangePassword = () => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state: RootState) => state.app);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Password strength calculation
  const getPasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: "", color: "#E5E7EB" };

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;

    if (score <= 2)
      return { score: 33, label: i18n.t("weak"), color: "#EF4444" };
    if (score <= 3)
      return { score: 66, label: i18n.t("medium"), color: "#F59E0B" };
    return { score: 100, label: i18n.t("strong"), color: "#10B981" };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  // Update locale when screen is focused
  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch])
  );

  const handleSave = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      Toast.show(i18n.t("fillAllFields"), { type: "danger" });
      return;
    }

    if (newPassword.length < 5) {
      Toast.show(i18n.t("passwordMinLength"), { type: "danger" });
      return;
    }

    if (newPassword !== confirmPassword) {
      Toast.show(i18n.t("passwordsNotMatch"), { type: "danger" });
      return;
    }

    if (currentPassword === newPassword) {
      Toast.show(i18n.t("passwordCannotBeSame"), { type: "danger" });
      return;
    }

    setLoading(true);
    try {
      const response = await customAxios.patch(
        `/users/updatepassword`,
        {
          password: currentPassword,
          newpassword: newPassword,
        },
        { withCredentials: true }
      );

      Toast.show(response.data?.message || i18n.t("passwordChangeSuccess"), {
        type: "success",
      });

      // Clear form fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      router.back();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("changePasswordError");

      Toast.show(errorMessage, { type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  // Render web version on web platform
  if (Platform.OS === "web") {
    return <WebPasswordScreen />;
  }

  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: "#f5f5f5" }}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      {/* Standard Navigation */}
      <StandardNavigation
        title={i18n.t("changePassword")}
        showBackButton={true}
        showMenuButton={true}
        showLogo={true}
        icon={<MaterialIcons name="lock" size={20} color="white" />}
      />

      <ScrollView
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingTop: 20,
          paddingBottom: 120, // Increased padding for button space
        }}
        keyboardShouldPersistTaps="handled"
      >
        {/* Info Box */}
        <Animated.View entering={FadeInDown.duration(400).delay(50)} className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200">
          <View
            className={`flex-row items-start ${
              locale === "ar" ? "flex-row-reverse" : "flex-row"
            }`}
          >
            <MaterialIcons name="info" size={20} color="#3B82F6" />
            <Text
              className={`flex-1 text-sm text-blue-800 ${
                locale === "ar" ? "mr-3 text-right" : "ml-3 text-left"
              }`}
              style={{ lineHeight: 20 }}
            >
              {i18n.t("changePasswordInfo")}
            </Text>
          </View>
        </Animated.View>

        {/* Current Password Field */}
        <Animated.View entering={FadeInDown.duration(400).delay(100)} className="mb-6">
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("currentPasswordLabel")}
          </Text>
          <View className="bg-white rounded-full px-6 py-4 border border-gray-100 flex-row items-center">
            <TouchableOpacity
              onPress={() => setShowCurrentPassword(!showCurrentPassword)}
              className={locale === "ar" ? "ml-3" : "mr-3"}
            >
              <Feather
                name={showCurrentPassword ? "eye" : "eye-off"}
                size={20}
                color="#6B7280"
              />
            </TouchableOpacity>
            <TextInput
              style={{
                flex: 1,
                fontSize: 16,
                textAlign: locale === "ar" ? "right" : "left",
                color: "#1F2937",
              }}
              value={currentPassword}
              onChangeText={setCurrentPassword}
              secureTextEntry={!showCurrentPassword}
              autoCapitalize="none"
              placeholder="••••••••••••••••"
              placeholderTextColor="#9CA3AF"
            />
          </View>
        </Animated.View>

        {/* New Password Field */}
        <Animated.View entering={FadeInDown.duration(400).delay(200)} className="mb-6">
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("newPassword")}
          </Text>
          <View className="bg-white rounded-full px-6 py-4 border border-gray-100 flex-row items-center">
            <TouchableOpacity
              onPress={() => setShowNewPassword(!showNewPassword)}
              className={locale === "ar" ? "ml-3" : "mr-3"}
            >
              <Feather
                name={showNewPassword ? "eye" : "eye-off"}
                size={20}
                color="#6B7280"
              />
            </TouchableOpacity>
            <TextInput
              style={{
                flex: 1,
                fontSize: 16,
                textAlign: locale === "ar" ? "right" : "left",
                color: "#1F2937",
              }}
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry={!showNewPassword}
              autoCapitalize="none"
              placeholder="••••••••••••••••"
              placeholderTextColor="#9CA3AF"
            />
          </View>

          {/* Password Strength Indicator */}
          {newPassword.length > 0 && (
            <View className="mt-3 px-2">
              <View
                className={`flex-row items-center justify-between mb-2 ${
                  locale === "ar" ? "flex-row-reverse" : "flex-row"
                }`}
              >
                <Text className="text-xs text-gray-600">
                  {i18n.t("passwordStrength")}:
                </Text>
                <Text
                  className="text-xs font-semibold"
                  style={{ color: passwordStrength.color }}
                >
                  {passwordStrength.label}
                </Text>
              </View>
              <View className="bg-gray-200 rounded-full h-2">
                <View
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${passwordStrength.score}%`,
                    backgroundColor: passwordStrength.color,
                  }}
                />
              </View>

              {/* Password Requirements */}
              <View className="mt-3 space-y-1">
                <View
                  className={`flex-row items-center ${
                    locale === "ar" ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <MaterialIcons
                    name={newPassword.length >= 8 ? "check-circle" : "cancel"}
                    size={16}
                    color={newPassword.length >= 8 ? "#10B981" : "#9CA3AF"}
                  />
                  <Text
                    className={`text-xs ${locale === "ar" ? "mr-2" : "ml-2"}`}
                    style={{
                      color: newPassword.length >= 8 ? "#10B981" : "#6B7280",
                    }}
                  >
                    {i18n.t("atLeast8Characters")}
                  </Text>
                </View>
                <View
                  className={`flex-row items-center ${
                    locale === "ar" ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <MaterialIcons
                    name={
                      /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword)
                        ? "check-circle"
                        : "cancel"
                    }
                    size={16}
                    color={
                      /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword)
                        ? "#10B981"
                        : "#9CA3AF"
                    }
                  />
                  <Text
                    className={`text-xs ${locale === "ar" ? "mr-2" : "ml-2"}`}
                    style={{
                      color:
                        /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword)
                          ? "#10B981"
                          : "#6B7280",
                    }}
                  >
                    {i18n.t("uppercaseAndLowercase")}
                  </Text>
                </View>
                <View
                  className={`flex-row items-center ${
                    locale === "ar" ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <MaterialIcons
                    name={/[0-9]/.test(newPassword) ? "check-circle" : "cancel"}
                    size={16}
                    color={/[0-9]/.test(newPassword) ? "#10B981" : "#9CA3AF"}
                  />
                  <Text
                    className={`text-xs ${locale === "ar" ? "mr-2" : "ml-2"}`}
                    style={{
                      color: /[0-9]/.test(newPassword) ? "#10B981" : "#6B7280",
                    }}
                  >
                    {i18n.t("atLeastOneNumber")}
                  </Text>
                </View>
              </View>
            </View>
          )}
        </Animated.View>

        {/* Confirm Password Field */}
        <Animated.View entering={FadeInDown.duration(400).delay(300)} className="mb-8">
          <Text
            className={`text-gray-600 text-base mb-2 ${
              locale === "ar" ? "text-right" : "text-left"
            }`}
          >
            {i18n.t("confirmPassword")}
          </Text>
          <View
            className={`bg-white rounded-full px-6 py-4 flex-row items-center ${
              confirmPassword.length > 0
                ? confirmPassword === newPassword
                  ? "border-2 border-green-400"
                  : "border-2 border-red-400"
                : "border border-gray-100"
            }`}
          >
            <TouchableOpacity
              onPress={() => setShowConfirmPassword(!showConfirmPassword)}
              className={locale === "ar" ? "ml-3" : "mr-3"}
            >
              <Feather
                name={showConfirmPassword ? "eye" : "eye-off"}
                size={20}
                color="#6B7280"
              />
            </TouchableOpacity>
            <TextInput
              style={{
                flex: 1,
                fontSize: 16,
                textAlign: locale === "ar" ? "right" : "left",
                color: "#1F2937",
              }}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry={!showConfirmPassword}
              autoCapitalize="none"
              placeholder="••••••••••••••••"
              placeholderTextColor="#9CA3AF"
            />
            {confirmPassword.length > 0 && (
              <MaterialIcons
                name={
                  confirmPassword === newPassword ? "check-circle" : "cancel"
                }
                size={20}
                color={confirmPassword === newPassword ? "#10B981" : "#EF4444"}
                style={{
                  marginLeft: locale === "ar" ? 0 : 8,
                  marginRight: locale === "ar" ? 8 : 0,
                }}
              />
            )}
          </View>
          {confirmPassword.length > 0 && confirmPassword !== newPassword && (
            <Text
              className={`text-xs mt-2 text-red-600 ${
                locale === "ar" ? "text-right" : "text-left"
              }`}
            >
              {i18n.t("passwordsNotMatch")}
            </Text>
          )}
          {confirmPassword.length > 0 && confirmPassword === newPassword && (
            <Text
              className={`text-xs mt-2 text-green-600 ${
                locale === "ar" ? "text-right" : "text-left"
              }`}
            >
              {i18n.t("passwordsMatch")}
            </Text>
          )}
        </Animated.View>
      </ScrollView>

      {/* Save Button - Fixed at bottom */}
      <Animated.View
        entering={FadeInDown.duration(400).delay(400)}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "white",
          paddingHorizontal: 24,
          paddingVertical: 16,
        }}
      >
        <TouchableOpacity
          onPress={handleSave}
          disabled={loading}
          style={{
            borderRadius: 25,
            overflow: "hidden",
            shadowColor: "#000",
            shadowOffset: {
              width: 0,
              height: 4,
            },
            shadowOpacity: 0.15,
            shadowRadius: 6,
            elevation: 8,
          }}
        >
          <LinearGradient
            colors={["#2196F3", "#1976D2"]}
            style={{
              paddingVertical: 16,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {loading ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <Text className="text-white text-lg font-bold">
                {i18n.t("save")}
              </Text>
            )}
          </LinearGradient>
        </TouchableOpacity>
      </Animated.View>
    </KeyboardAvoidingView>
  );
};

export default ChangePassword;
